import React, { FC, useMemo } from 'react';
import { Col, Row, Form } from 'antd';
import { useTranslate, useOne } from '@refinedev/core';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import { DatePicker, FormItem, Checkbox } from 'components/ui';
import { ICategory } from 'interfaces/application.interface';
import { IProcedureItem } from 'interfaces/procedure.interface';
import styles from './steps.module.scss';

type ExistingFile = {
    id?: string | number;
    name?: string;
    original_name?: string;
    file_name?: string;
    filename?: string;
    created_at?: string;
    createdAt?: string;
    updated_at?: string;
    size?: number | string;
    file_size?: number | string;
};

type DisplayFile = {
    key: string;
    name: string;
    size?: number;
    date?: Date | string;
};

interface Step5SubmissionProps {
    fileList: UploadFile[];
    existingFiles?: ExistingFile[];
    showRegDateField?: boolean;
    showInternationalName?: boolean;
    showProcedureInfo?: boolean;
    showCheckboxes?: boolean;
}

export const Step5Submission: FC<Step5SubmissionProps> = ({
    fileList,
    existingFiles = [],
    showRegDateField = true,
    showInternationalName = true,
    showProcedureInfo = true,
    showCheckboxes = true,
}) => {
    const t = useTranslate();
    const form = Form.useFormInstance();

    const watchedValues = Form.useWatch([], form);
    const formValues = useMemo(() => {
        const snapshot = form?.getFieldsValue?.(true) ?? {};
        return {
            ...snapshot,
            ...(watchedValues ?? {}),
        };
    }, [form, watchedValues]);

    const medicationName = formValues?.medication_name;
    const releaseForm = formValues?.release_form;
    const internationalNameId = formValues?.international_name_id;
    const certificate = formValues?.certificate;
    const applicantId = formValues?.applicant_id;
    const notificationEmail = formValues?.notification_email;
    const procedureId = formValues?.procedure_id;
    const procedureDesc = formValues?.procedure_desc;

    const { data: internationalNameData } = useOne<ICategory>({
        resource: 'dictionary/international-names',
        id: internationalNameId ?? '',
        queryOptions: {
            enabled: Boolean(internationalNameId),
        },
    });

    const { data: applicantData } = useOne<ICategory>({
        resource: 'dictionary/applicants',
        id: applicantId ?? '',
        queryOptions: {
            enabled: Boolean(applicantId),
        },
    });

    const { data: procedureData } = useOne<IProcedureItem>({
        resource: 'dictionary/preliminary_request_procedure',
        id: procedureId ?? '',
        queryOptions: {
            enabled: Boolean(procedureId),
        },
    });

    const resolvedInternationalName = useMemo(() => {
        return (
            internationalNameData?.data?.name ??
            (internationalNameId ? String(internationalNameId) : '—')
        );
    }, [internationalNameData?.data?.name, internationalNameId]);

    const resolvedApplicant = useMemo(() => {
        const applicant = applicantData?.data as
            | (ICategory & { full_name?: string })
            | undefined;

        return (
            applicant?.name ??
            applicant?.full_name ??
            (applicantId ? String(applicantId) : '—')
        );
    }, [applicantData?.data, applicantId]);

    const resolvedProcedure = useMemo(() => {
        return (
            procedureData?.data?.name_for_applicant ??
            procedureData?.data?.name ??
            (procedureId ? String(procedureId) : '—')
        );
    }, [
        procedureData?.data?.name,
        procedureData?.data?.name_for_applicant,
        procedureId,
    ]);

    const parseSize = (value: unknown): number | undefined => {
        if (typeof value === 'number' && !Number.isNaN(value)) {
            return value;
        }

        if (typeof value === 'string') {
            const parsed = Number(value);
            return Number.isNaN(parsed) ? undefined : parsed;
        }

        return undefined;
    };

    const parseDate = (value: unknown): Date | undefined => {
        if (!value) return undefined;

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value;
        }

        const date =
            typeof value === 'number'
                ? new Date(value)
                : new Date(String(value));

        return Number.isNaN(date.getTime()) ? undefined : date;
    };

    const formatFileSize = (bytes?: number): string | undefined => {
        if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
            return undefined;
        }

        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i)) + sizes[i];
    };

    const formatFileDate = (value?: Date | string): string | undefined => {
        if (!value) return undefined;
        const date = dayjs(value);
        return date.isValid() ? date.format('DD MMM, YYYY | HH:mm') : undefined;
    };

    const normalizedExistingFiles = useMemo<DisplayFile[]>(() => {
        return (existingFiles ?? []).map((file, index) => {
            const resolvedName =
                file?.name ??
                file?.original_name ??
                file?.file_name ??
                file?.filename ??
                t(
                    'preliminary-applications.fields.unnamed_file',
                    'Файл без назви'
                );

            return {
                key: `existing-${file?.id ?? `${resolvedName}-${index}`}`,
                name: resolvedName,
                size: parseSize(file?.size ?? file?.file_size),
                date:
                    file?.created_at ??
                    file?.createdAt ??
                    file?.updated_at ??
                    undefined,
            };
        });
    }, [existingFiles, t]);

    const normalizedNewFiles = useMemo<DisplayFile[]>(() => {
        return (fileList ?? []).map(file => {
            const normalizedDate =
                parseDate((file as any).lastModifiedDate) ??
                parseDate((file as any).originFileObj?.lastModifiedDate) ??
                parseDate((file as any).originFileObj?.lastModified) ??
                new Date();

            return {
                key: `local-${file.uid}`,
                name: file.name,
                size: parseSize((file as any).size ?? file.size),
                date: normalizedDate,
            };
        });
    }, [fileList]);

    const combinedFiles = useMemo<DisplayFile[]>(() => {
        return [...normalizedExistingFiles, ...normalizedNewFiles];
    }, [normalizedExistingFiles, normalizedNewFiles]);

    const resolveMeta = (file: DisplayFile): string => {
        const dateLabel = formatFileDate(file.date);
        const sizeLabel = formatFileSize(file.size);
        const parts = [dateLabel, sizeLabel].filter(Boolean);

        return parts.join(' • ');
    };

    return (
        <div className={styles.stepContainer}>
            <h2 className={styles.stepTitle}>
                {t('preliminary-applications.wizard.step5.title', 'Відправка')}
            </h2>

            <Row gutter={[16, 20]}>
                {showRegDateField && (
                    <Col xs={24}>
                        <FormItem
                            name="reg_date"
                            rules={[
                                {
                                    required: true,
                                    message: t(
                                        'validation.required',
                                        'This field is required'
                                    ),
                                },
                            ]}
                            style={{
                                display: showRegDateField ? undefined : 'none',
                            }}
                        >
                            <DatePicker
                                format="DD.MM.YYYY"
                                style={{ width: '100%' }}
                                label={t(
                                    'preliminary-applications.fields.reg_date',
                                    'Дата'
                                )}
                                placeholder={t(
                                    'preliminary-applications.fields.select_date',
                                    'Виберіть дату'
                                )}
                                requiredMark
                            />
                        </FormItem>
                    </Col>
                )}

                <Col xs={24}>
                    <div className={styles.summaryContainer}>
                        {/* Drug Info Block */}
                        <div>
                            <h3 className={styles.blockHeading}>
                                {t(
                                    'preliminary-applications.wizard.step1.title',
                                    'Відомості про препарат'
                                )}
                            </h3>
                            <div className={styles.summaryBlock}>
                                <dl className={styles.summaryList}>
                                    <div className={styles.summaryItem}>
                                        <dt className={styles.summaryLabel}>
                                            {t(
                                                'preliminary-applications.fields.medication_name',
                                                'Назва лікарського засобу'
                                            )}
                                            *
                                        </dt>
                                        <dd className={styles.summaryValue}>
                                            {medicationName || '—'}
                                        </dd>
                                    </div>
                                    <div className={styles.summaryItem}>
                                        <dt className={styles.summaryLabel}>
                                            {t(
                                                'preliminary-applications.fields.release_form',
                                                'Форма випуску'
                                            )}
                                        </dt>
                                        <dd className={styles.summaryValue}>
                                            {releaseForm || '—'}
                                        </dd>
                                    </div>
                                    {showInternationalName && (
                                        <div className={styles.summaryItem}>
                                            <dt className={styles.summaryLabel}>
                                                {t(
                                                    'preliminary-applications.fields.active_ingredient_inn',
                                                    'Дієва (-і) речовина (-и) (МНН)'
                                                )}
                                            </dt>
                                            <dd className={styles.summaryValue}>
                                                {resolvedInternationalName}
                                            </dd>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        </div>

                        {/* Certificate & Procedure Block */}
                        <div>
                            <h3 className={styles.blockHeading}>
                                {t(
                                    'preliminary-applications.wizard.step2.title',
                                    'Відомості про посвідчення та процедуру'
                                )}
                            </h3>
                            <div className={styles.summaryBlock}>
                                <dl className={styles.summaryList}>
                                    <div className={styles.summaryItem}>
                                        <dt className={styles.summaryLabel}>
                                            {t(
                                                'preliminary-applications.fields.certificate',
                                                'Реєстраційне посвідчення'
                                            )}
                                            *
                                        </dt>
                                        <dd className={styles.summaryValue}>
                                            {certificate || '—'}
                                        </dd>
                                    </div>
                                    <div className={styles.summaryItem}>
                                        <dt className={styles.summaryLabel}>
                                            {t(
                                                'preliminary-applications.fields.applicant',
                                                'Заявник (власник реєстраційного посвідчення)'
                                            )}
                                        </dt>
                                        <dd className={styles.summaryValue}>
                                            {resolvedApplicant}
                                        </dd>
                                    </div>
                                    <div className={styles.summaryItem}>
                                        <dt className={styles.summaryLabel}>
                                            {t(
                                                'preliminary-applications.fields.notification_email',
                                                'Email для повідомлень'
                                            )}
                                        </dt>
                                        <dd className={styles.summaryValue}>
                                            {notificationEmail || '—'}
                                        </dd>
                                    </div>
                                    {showProcedureInfo && (
                                        <>
                                            <div className={styles.summaryItem}>
                                                <dt
                                                    className={
                                                        styles.summaryLabel
                                                    }
                                                >
                                                    {t(
                                                        'preliminary-applications.fields.procedure',
                                                        'Процедура'
                                                    )}
                                                    *
                                                </dt>
                                                <dd
                                                    className={
                                                        styles.summaryValue
                                                    }
                                                >
                                                    {resolvedProcedure}
                                                </dd>
                                            </div>
                                            <div className={styles.summaryItem}>
                                                <dt
                                                    className={
                                                        styles.summaryLabel
                                                    }
                                                >
                                                    {t(
                                                        'preliminary-applications.fields.procedure_desc',
                                                        'Опис процедури'
                                                    )}
                                                    *
                                                </dt>
                                                <dd
                                                    className={
                                                        styles.summaryValue
                                                    }
                                                >
                                                    {procedureDesc || '—'}
                                                </dd>
                                            </div>
                                        </>
                                    )}
                                </dl>
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Checkboxes - Synced with Step 3 */}
                {showCheckboxes && (
                    <Col xs={24}>
                        <div className={styles.checkboxGroup}>
                            <FormItem
                                name="is_electronic_form"
                                valuePropName="checked"
                            >
                                <Checkbox
                                    label={t(
                                        'preliminary-applications.fields.electronic_form',
                                        'Подано в електронному вигляді'
                                    )}
                                />
                            </FormItem>
                            <FormItem name="is_ectd" valuePropName="checked">
                                <Checkbox
                                    label={t(
                                        'preliminary-applications.fields.ectd',
                                        'eCTD'
                                    )}
                                />
                            </FormItem>
                            <FormItem name="is_mibp" valuePropName="checked">
                                <Checkbox
                                    label={t(
                                        'preliminary-applications.fields.mibp',
                                        'МІВП'
                                    )}
                                />
                            </FormItem>
                        </div>
                    </Col>
                )}

                {/* Files Section - No Border */}
                <Col xs={24}>
                    <div className={styles.filesSection}>
                        <div className={styles.filesSectionHeader}>
                            <h3>
                                {t(
                                    'preliminary-applications.fields.uploaded_files',
                                    'Завантажені файли'
                                )}
                            </h3>
                            <span className={styles.filesCount}>
                                {combinedFiles.length}
                            </span>
                        </div>

                        {combinedFiles.length ? (
                            <ul className={styles.filesList}>
                                {combinedFiles.map(file => (
                                    <li
                                        key={file.key}
                                        className={styles.fileItem}
                                    >
                                        <span
                                            className={styles.fileItemIcon}
                                            aria-hidden
                                        />
                                        <div className={styles.fileInfo}>
                                            <div className={styles.fileName}>
                                                {file.name}
                                            </div>
                                            <div className={styles.fileMeta}>
                                                {resolveMeta(file)}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className={styles.emptyFiles}>
                                {t(
                                    'preliminary-applications.fields.no_files',
                                    'Файлів ще не завантажено'
                                )}
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
};
